import { OrganizationService } from './../service/organization.service';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Observable, Subject, fromEvent, map, filter, debounceTime, distinctUntilChanged } from 'rxjs';
import { Region } from '../../../../model/region.model';
import { MsgModel } from '../../msg/model/msg.model';
import { Organization } from '../model/organization.model';

@Component({
    selector: 'input-organization-autocomplete',
    templateUrl: './input-organization-autocomplete.component.html',
    styleUrls: ['./input-organization-autocomplete.component.scss']
})
export class InputOrganizationAutocompleteComponent implements OnInit {
    @Input() id: string = 'Organization';
    @Input() name: string = 'Organization';
    @Input() label: string = 'Organization';
    @Input() placeholder: string = 'Organization';
    @Input() hint: string = '';
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;
    @Input() searchCategory: string = '';
    @Input() selectedOrganization: Organization = { NAME: '' };
    @Input() additionalErrorMsg: string = '';
    @Output() selectedOrganizationChange: EventEmitter<Organization> = new EventEmitter<Organization>();

    @Input() region: Region | undefined;
    @Output() regionChange: EventEmitter<Region> = new EventEmitter<Region>();

    @Input() regionId!: number;

    @ViewChild('searchString') searchString!: NgModel;
    @ViewChild('searchStringInput', { static: true }) searchStringInput!: ElementRef;
    searchStringChanged$: Observable<any> = new Subject<any>();
    inputString: string = '';
    localOrganization: Organization = { NAME: '' };

    isLoading: boolean = false;
    msg!: MsgModel;

    minCharacters = 3;

    organizations!: Organization[];

    constructor(private organizationService: OrganizationService) {}

    ngOnInit(): void {
        // Search by ID if given an ID
        if (this.selectedOrganization && this.selectedOrganization.ID! > 0) {
            this.isLoading = true;
            this.show(this.selectedOrganization.ID!).subscribe((res: any) => {
                this.isLoading = false;
                if (res['organization'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    this.onSelect(res['organization']);
                }
            });
        }
        // Search by NAME w/ no ID
        else if (this.selectedOrganization && this.selectedOrganization.NAME && this.selectedOrganization.NAME.length > this.minCharacters) {
            this.isLoading = true;
            this.search(this.selectedOrganization.NAME).subscribe((res: any) => {
                this.isLoading = false;
                if (res['organizations'] == undefined) {
                    if (res['msg'] !== undefined) {
                        this.msg = res['msg'];
                    }
                } else {
                    if (res.organizations.length === 1) {
                        this.onSelect(res['organizations']);
                    } else {
                        for (var i = 0; i < res.organizations.length; i++) {
                            if (res.organizations[i].NAME === this.selectedOrganization.NAME) {
                                this.onSelect(res.organizations[i]);
                                break;
                            }
                        }
                    }
                }
            });
        }

        fromEvent(this.searchStringInput.nativeElement, 'keyup')
            .pipe(
                // get value
                map((event: any) => {
                    return event.target.value;
                }),
                // only search when value has changed
                distinctUntilChanged(),
                // 500 ms delay before searching
                debounceTime(500),
                // only search if search string is >= 3 characters
                filter((res) => {
                    let canSearch = res !== null && res.length >= this.minCharacters;

                    let newOrganization = new Organization();
                    newOrganization.NAME = res;
                    this.onSelect(newOrganization);

                    return canSearch;
                })
            )
            .subscribe((text: string) => {
                this.isLoading = true;

                switch (this.searchCategory) {
                    case 'Schools':
                        this.searchSchools(text).subscribe((res: any) => {
                            this.isLoading = false;
                            if (res['organizations'] == undefined) {
                                let newOrganization = new Organization();
                                newOrganization.NAME = text;
                                this.onSelect(newOrganization);
                                this.organizations = [];
                            } else {
                                this.organizations = res['organizations'];
                                this.autoSelect(this.organizations);
                            }
                        });
                        break;
                    case 'Legal Organizations':
                        this.searchLegalOrganizations(text).subscribe((res: any) => {
                            this.isLoading = false;
                            if (res['organizations'] == undefined) {
                                let newOrganization = new Organization();
                                newOrganization.NAME = text;
                                this.onSelect(newOrganization);
                                this.organizations = [];
                            } else {
                                this.organizations = res['organizations'];
                                this.autoSelect(this.organizations);
                            }
                        });
                        break;
                    case 'MSIN Schools':
                        this.searchMSINSchools(text).subscribe((res: any) => {
                            this.isLoading = false;
                            if (res['organizations'] == undefined) {
                                let newOrganization = new Organization();
                                newOrganization.NAME = text;
                                this.onSelect(newOrganization);
                                this.organizations = [];
                            } else {
                                this.organizations = res['organizations'];
                                this.autoSelect(this.organizations);
                            }
                        });
                        break;
                    case 'Regions':
                        this.searchRegions(text).subscribe((res: any) => {
                            this.isLoading = false;
                            if (res['organizations'] == undefined) {
                                let newOrganization = new Organization();
                                newOrganization.NAME = text;
                                this.onSelect(newOrganization);
                                this.organizations = [];
                            } else {
                                this.organizations = res['organizations'];
                                this.autoSelect(this.organizations);
                            }
                        });
                        break;
                    case 'School Districts':
                        this.searchSchoolDistricts(text).subscribe((res: any) => {
                            this.isLoading = false;
                            if (res['organizations'] == undefined) {
                                let newOrganization = new Organization();
                                newOrganization.NAME = text;
                                this.onSelect(newOrganization);
                                this.organizations = [];
                            } else {
                                this.organizations = res['organizations'];
                                this.autoSelect(this.organizations);
                            }
                        });
                        break;
                    case 'No Region':
                        this.searchNoRegion(text).subscribe((res: any) => {
                            this.isLoading = false;
                            if (res['organizations'] == undefined) {
                                let newOrganization = new Organization();
                                newOrganization.NAME = text;
                                this.onSelect(newOrganization);
                                this.organizations = [];
                            } else {
                                this.organizations = res['organizations'];
                                this.autoSelect(this.organizations);
                            }
                        });
                        break;
                    case 'None':
                        this.searchNone(text).subscribe((res: any) => {
                            this.isLoading = false;
                            if (res['organizations'] == undefined) {
                                let newOrganization = new Organization();
                                newOrganization.NAME = text;
                                this.onSelect(newOrganization);
                                this.organizations = [];
                            } else {
                                this.organizations = res['organizations'];
                                this.autoSelect(this.organizations);
                            }
                        });
                        break;
                    case 'Not A Region':
                        this.searchNotARegion(text).subscribe((res: any) => {
                            this.isLoading = false;
                            if (res['organizations'] == undefined) {
                                let newOrganization = new Organization();
                                newOrganization.NAME = text;
                                this.onSelect(newOrganization);
                                this.organizations = [];
                            } else {
                                this.organizations = res['organizations'];
                                this.autoSelect(this.organizations);
                            }
                        });
                        break;
                    default:
                        this.search(text).subscribe((res: any) => {
                            this.isLoading = false;
                            if (res['organizations'] == undefined) {
                                let newOrganization = new Organization();
                                newOrganization.NAME = text;
                                this.onSelect(newOrganization);
                                this.organizations = [];
                            } else {
                                this.organizations = res['organizations'];
                                this.autoSelect(this.organizations);
                            }
                        });
                        break;
                }
            });
    }

    autoSelect(organizations?: Organization[]) {
        if (!organizations) {
            return;
        } else if (organizations.length === 0) {
            return;
        } else if (organizations.length === 1) {
            for (var i = 0; i < organizations.length; i++) {
                if (organizations[i].NAME === this.localOrganization.NAME) {
                    this.onSelect(organizations[i]);
                    break;
                }
            }
        }
    }

    // By ID
    show(id: any): Observable<Organization[]> {
        return this.organizationService.show(id);
    }

    // With Region
    search(searchString: any): Observable<Organization[]> {
        return this.organizationService.search(searchString, this.getRegion());
    }
    searchSchools(searchString: any): Observable<Organization[]> {
        return this.organizationService.searchSchools(searchString, this.getRegion());
    }
    searchLegalOrganizations(searchString: any): Observable<Organization[]> {
        return this.organizationService.searchLegalOrganizations(searchString, this.getRegion());
    }
    searchMSINSchools(searchString: any): Observable<Organization[]> {
        return this.organizationService.searchMSINSchools(searchString, this.getRegion());
    }
    searchSchoolDistricts(searchString: any): Observable<Organization[]> {
        return this.organizationService.searchSchoolDistricts(searchString, this.getRegion());
    }

    // Without Region
    searchRegions(searchString: any): Observable<Organization[]> {
        return this.organizationService.searchRegions(searchString);
    }
    searchNoRegion(searchString: any): Observable<Organization[]> {
        return this.organizationService.searchNoRegion(searchString);
    }
    searchNone(searchString: any): Observable<Organization[]> {
        return this.organizationService.searchNone(searchString);
    }
    searchNotARegion(searchString: any): Observable<Organization[]> {
        return this.organizationService.searchNotARegion(searchString);
    }

    onSelect(organization: Organization) {
        this.selectedOrganization = organization;
        this.selectedOrganizationChange.emit(this.selectedOrganization);

        this.localOrganization = JSON.parse(JSON.stringify(this.selectedOrganization));

        this.checkForId(this.searchString);

        this.clearAutocompleteArray();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedOrganization'] && changes['selectedOrganization'].currentValue) {
            this.localOrganization = JSON.parse(JSON.stringify(changes['selectedOrganization'].currentValue));
        }
    }

    checkForId(input: NgModel) {
        let errorMsg = 'Please search and select a valid ' + this.name + '.' + (this.additionalErrorMsg.length > 0 ? ' ' + this.additionalErrorMsg : '');

        if (this.required) {
            if (this.localOrganization.NAME && this.localOrganization.NAME.length === 0) {
                input.control.setErrors({ Required: errorMsg });
            }
            if (!this.localOrganization.ID) {
                input.control.setErrors({ idRequired: errorMsg });
            } else {
                input.control.setErrors(null);
            }
        } else {
            if (this.localOrganization.NAME && this.localOrganization.NAME.length > 0 && !this.localOrganization.ID) {
                input.control.setErrors({ idRequired: errorMsg });
            } else {
                input.control.setErrors(null);
            }
        }
    }

    clearAutocompleteArray() {
        this.organizations = [];
    }

    getRegion() {
        if (this.region?.ID) {
            return this.region;
        } else if (this.regionId) {
            this.region = new Region();
            this.region.ID = this.regionId;

            return this.region;
        } else {
            return undefined;
        }
    }
}
